import { FullCommission } from 'apis/offer-commissions-service/types';
import { Product } from 'apis/offer-listing-service/types';
import {
  AgeRestriction,
  CourierRestriction,
  ItemDisplayUnit,
  NutritionSetting,
  OTCRestriction,
  ProductInformationSettings,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { VatCategory } from 'apis/vat-categories/types';
import {
  EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY,
  fromWeeklyScheduleInputEntriesToWeeklyTimeSpans,
  fromWeeklyTimeSpansToWeeklyScheduleInputEntries,
} from 'common/components/weekly-schedules';
import { isDefined } from 'common/utils';
import { initialVatFieldValue, VatFieldValue } from 'modules/menu/components/VatCategories';
import { AdvancedDetails } from 'modules/menu/items/components/ItemDetailsForm/types';
import { LocalisedValue, OfferOverrides } from 'modules/shared-menu/types';
import { getTypedKeys } from 'modules/shared-menu/utils';

import { FeedFormValues, FormValues, InventoryManagementFormValues, OverrideFormValues } from './types';
import { alcoholPercentageToPermilles, getDiscountPercentage, getPercentageDiscountPrice } from './utils';
import { ValidFormValues } from './validationSchema';
import { Offer, OptionBindingAttrs, SharedMenuDietaryPreference, SharedMenuMandatoryWarning } from '../../types';
import { convertLocalIsoDateToUtcIsoDate, updatePrimaryLanguageTranslation } from '../../utils';

const findTranslation = (translations: LocalisedValue[] | undefined, language: Language) => {
  if (!translations) {
    return '';
  }
  return translations.find(t => t.lang === language)?.value || '';
};

const serializeEnabledDeliveryMethods = (values: ValidFormValues): DeliveryMethod[] => {
  const enabledDeliveryMethods: DeliveryMethod[] = [];

  if (values.enableEatin) {
    enabledDeliveryMethods.push('eatin');
  }

  if (values.enableTakeaway) {
    enabledDeliveryMethods.push('takeaway');
  }

  if (values.enableDelivery) {
    enabledDeliveryMethods.push('homedelivery');
  }

  return enabledDeliveryMethods;
};

const serializeCourierRestrictions = (values: ValidFormValues): CourierRestriction[] => {
  const courierRestrictions: CourierRestriction[] = [];

  if (values.disableBikeDelivery) {
    courierRestrictions.push('no_bikes');
  }

  if (values.disableCarDelivery) {
    courierRestrictions.push('no_cars');
  }

  if (values.disableMotorcycleDelivery) {
    courierRestrictions.push('no_motorcycles');
  }

  return courierRestrictions;
};

type GetBasePriceArgs = {
  price: ValidFormValues['price'] | NonNullable<ValidFormValues['priceOverride']>;
  currency: Currency;
  discountType: ValidFormValues['discountType'] | ValidFormValues['discountTypeOverride'];
  discountPercentage: ValidFormValues['discountPercentage'] | ValidFormValues['discountPercentageOverride'];
  fixedDiscountPrice: ValidFormValues['fixedDiscountPrice'] | ValidFormValues['fixedDiscountPriceOverride'];
};
const getBasePrice = ({ price, currency, discountType, discountPercentage, fixedDiscountPrice }: GetBasePriceArgs) => {
  switch (discountType) {
    case 'relative':
      if (typeof discountPercentage !== 'number') {
        throw Error('discountType is percentage but percentage is not a number');
      }
      return getPercentageDiscountPrice(price, discountPercentage, currency);
    case 'absolute':
      return String(fixedDiscountPrice);
    case undefined:
      return String(price);
  }
};

const getInventoryMode = (
  values: ValidFormValues,
  fieldName: keyof ValidFormValues = 'inventoryMode',
): Offer['inventory_mode'] => {
  if (values[fieldName] === 'binary') {
    const inStockKey = fieldName === 'inventoryMode' ? 'inStock' : 'inStockOverride';
    return values[inStockKey as keyof ValidFormValues] ? undefined : 'forced_out_of_stock';
  }
  return values[fieldName] as Offer['inventory_mode'];
};

/**
 * To avoid having 2 different functions for building the payload for the overrides and the payload for the regular Offer
 * We are keeping the transforming logic of fields that can be overridden in one place and reusing it for both the Offer payload and the OfferOverrides payload
 */
const sharedFieldsPayloadBuilder = {
  price: (priceArgs: GetBasePriceArgs) => Number(getBasePrice(priceArgs)),
  discounted: (
    discountType: ValidFormValues['discountType'] | ValidFormValues['discountTypeOverride'],
    price: ValidFormValues['price'] | ValidFormValues['priceOverride'],
  ) =>
    discountType && price
      ? {
          discount_type: discountType,
          original_price: price,
        }
      : undefined,
  enabled: (
    enabled: ValidFormValues['enabled'] | ValidFormValues['enabledOverride'],
    availabilityPeriod: ValidFormValues['availabilityPeriod'] | ValidFormValues['availabilityPeriodOverride'],
  ) => ({
    enabled: !!enabled,
    after: availabilityPeriod?.start ? convertLocalIsoDateToUtcIsoDate(availabilityPeriod.start) : undefined,
    before: availabilityPeriod?.end ? convertLocalIsoDateToUtcIsoDate(availabilityPeriod.end) : undefined,
  }),
  external_id: (value: ValidFormValues['posId'] | ValidFormValues['posIdOverride']) => value || undefined,
  merchant_sku: (value: ValidFormValues['merchantSku'] | ValidFormValues['merchantSkuOverride']) => value || undefined,
  weekly_availability: (
    useWeeklyAvailability: ValidFormValues['useWeeklyAvailability'] | ValidFormValues['useWeeklyAvailabilityOverride'],
    weeklyAvailability: ValidFormValues['weeklyAvailability'] | ValidFormValues['weeklyAvailabilityOverride'],
  ) =>
    useWeeklyAvailability && weeklyAvailability && weeklyAvailability.length > 0
      ? fromWeeklyScheduleInputEntriesToWeeklyTimeSpans(weeklyAvailability)
      : undefined,
  weekly_visibility: (
    useWeeklyVisibility: ValidFormValues['useWeeklyVisibility'] | ValidFormValues['useWeeklyVisibilityOverride'],
    weeklyVisibility: ValidFormValues['weeklyVisibility'] | ValidFormValues['weeklyVisibilityOverride'],
  ) =>
    useWeeklyVisibility && weeklyVisibility && weeklyVisibility.length > 0
      ? fromWeeklyScheduleInputEntriesToWeeklyTimeSpans(weeklyVisibility)
      : undefined,
};

export const getProductFamilyId = (
  hierarchyLevelId: string,
  productHierarchies: ProductInformationSettings['product_hierarchy_levels'],
) => {
  return productHierarchies.find(hierarchy => hierarchy.id === hierarchyLevelId)?.human_readable_id;
};

const transformOfferFormValuesToApiBaseAttrs = (
  values: ValidFormValues,
  settings: {
    currency: Currency;
    nutritionFactsFormat: 'text' | 'table';
    productHierarchies: ProductInformationSettings['product_hierarchy_levels'];
  },
) => {
  const { currency, nutritionFactsFormat, productHierarchies } = settings;

  const nutritionValues =
    nutritionFactsFormat === 'table' && values.nutritionValues
      ? values.nutritionValues
          .filter((value): value is { value: string; id: string } => !!value.value)
          .map(value => ({ amount: value.value, nutrition_value_id: value.id }))
      : undefined;

  return {
    alcohol_percentage: values.alcoholPercentage,
    barcode_gtin: values.barcodeGtin || undefined,
    brand_id: values.brandId || null,
    price: sharedFieldsPayloadBuilder.price({
      price: values.price,
      currency,
      discountType: values.discountType,
      discountPercentage: values.discountPercentage,
      fixedDiscountPrice: values.fixedDiscountPrice,
    }),
    courier_restrictions: serializeCourierRestrictions(values),
    dietary_preferences: values.dietaryPreferences,
    discounted: sharedFieldsPayloadBuilder.discounted(values.discountType, values.price),
    delivery_methods: serializeEnabledDeliveryMethods(values),
    deposit_permutation_id:
      values.weightType !== 'weighted-items' && values.depositOption ? values.depositOption : undefined,
    enabled: sharedFieldsPayloadBuilder.enabled(values.enabled, values.availabilityPeriod),
    external_id: sharedFieldsPayloadBuilder.external_id(values.posId),
    is_bundle_offer: values.bundleOffer,
    inventory_mode: getInventoryMode(values),
    mandatory_warnings: values.mandatoryWarnings,
    merchant_sku: sharedFieldsPayloadBuilder.merchant_sku(values.merchantSku),
    nutrition_values: nutritionValues && nutritionValues.length > 0 ? nutritionValues : undefined,
    nutrition_values_baseline: (nutritionFactsFormat === 'table' && values.nutritionValuesBaseline) || undefined,
    product_hierarchy_level_id: values.productHierarchyLevelId ?? undefined,
    product_family_id: values.productHierarchyLevelId
      ? getProductFamilyId(values.productHierarchyLevelId, productHierarchies)
      : undefined,
    product_id: values.productId,
    source_config: values.source
      ? {
          default_source: values.source || undefined,
          field_config: {
            additives: values.sourceConfig?.additives,
            alcohol_percentage: values.sourceConfig?.alcoholPercentage,
            allergens: values.sourceConfig?.allergens,
            description: values.sourceConfig?.description,
            dietary_preferences: values.sourceConfig?.dietaryPreferences,
            distributor_information: values.sourceConfig?.distributorInfo,
            image_url: values.sourceConfig?.image,
            ingredients: values.sourceConfig?.ingredients,
            mandatory_warnings: values.sourceConfig?.mandatoryWarnings,
            more_information: values.sourceConfig?.moreInformation,
            name: values.sourceConfig?.name,
            number_of_units: values.sourceConfig?.numberOfUnits,
            nutrition_facts: values.sourceConfig?.nutritionFields,
            nutrition_values_baseline: values.sourceConfig?.nutritionFields,
            nutrition_values: values.sourceConfig?.nutritionFields,
            country_of_origin: values.sourceConfig?.countryOfOrigin,
            display_unit: values.sourceConfig?.displayUnit,
            conditions_of_use_and_storage: values.sourceConfig?.usageInstructions,
            producer_information: values.sourceConfig?.producerInfo,
            volume_in_ml: values.sourceConfig?.volumeInMl,
            weight_in_grams: values.sourceConfig?.weightInGrams,
            product_hierarchy_level_id: values.sourceConfig?.productHierarchyLevelId,
          },
        }
      : undefined,
    user_requirements: values.userRequirement ? [values.userRequirement] : undefined,
    vat_percentage: String(values.vat.percentage),
    vat_category_code: (values.vat.type === 'categories' && values.vat.categoryCode) || undefined,
    weekly_availability: sharedFieldsPayloadBuilder.weekly_availability(
      values.useWeeklyAvailability,
      values.weeklyAvailability,
    ),
    weekly_visibility: sharedFieldsPayloadBuilder.weekly_visibility(
      values.useWeeklyVisibility,
      values.weeklyVisibility,
    ),
    number_of_units: values.weightType === 'pre-packaged' && values.numberOfUnits ? values.numberOfUnits : undefined,
    volume_in_ml: values.weightType === 'pre-packaged' && values.volumeInMl ? values.volumeInMl : undefined,
    weight_in_grams: values.weightType === 'pre-packaged' && values.weightInGrams ? values.weightInGrams : undefined,
    display_unit: values.displayUnit ?? undefined,
    max_quantity_per_purchase: values.maxQuantityPerPurchase || undefined,
    min_quantity_per_purchase: values.minQuantityPerPurchase || undefined,
    sell_by_weight_config:
      values.weightType === 'weighted-items' && values.sellByWeightInputType && values.gramsPerStep
        ? {
            grams_per_step: values.gramsPerStep,
            input_type: values.sellByWeightInputType,
          }
        : undefined,
    wolt_plus_only: !!values.isWoltPlusOnly,
  };
};

export const sanitizeOptionBinding = (optionBinding: OptionBindingAttrs) => {
  return {
    ...optionBinding,
    name: optionBinding.name && optionBinding.name.length > 0 ? optionBinding.name : undefined,
    prerequisite_values:
      optionBinding.prerequisite_values && optionBinding.prerequisite_values.length > 0
        ? optionBinding.prerequisite_values
        : undefined,
  };
};

export type TransformOfferFormValuesToApiSettingsArg = {
  language: Language;
  currency: Currency;
  nutritionFactsFormat: 'text' | 'table';
  productHierarchies: ProductInformationSettings['product_hierarchy_levels'];
  externalInfoHyperlinkAllowed: boolean;
};

export const transformOfferFormValuesToApi = (
  offer: Offer | null,
  values: ValidFormValues,
  imageUrls: string[],
  settings: TransformOfferFormValuesToApiSettingsArg,
): any => {
  const { language, currency, nutritionFactsFormat, productHierarchies } = settings;

  // Strip client-side created IDs from newly created option bindings
  const optionBindings = values.optionBindings?.map(binding => {
    const originalBinding = (offer?.option_bindings || []).find(itemBinding => itemBinding.id === binding.id);
    if (originalBinding) {
      return sanitizeOptionBinding(binding);
    }
    const { id, ...optionBindingAttrs } = binding;

    return sanitizeOptionBinding(optionBindingAttrs);
  });

  const baseAttributes = transformOfferFormValuesToApiBaseAttrs(values, {
    currency,
    nutritionFactsFormat,
    productHierarchies,
  });
  return {
    ...baseAttributes,
    additives: values.additives
      ? updatePrimaryLanguageTranslation(language, values.additives, offer?.additives)
      : undefined,
    allergens: values.allergens
      ? updatePrimaryLanguageTranslation(language, values.allergens, offer?.allergens)
      : undefined,
    caffeine_content:
      values?.caffeineContentValue && values?.caffeineContentUnits
        ? {
            value: values.caffeineContentValue,
            units: values.caffeineContentUnits,
          }
        : null,
    conditions_of_use_and_storage: values.usageInstructions
      ? updatePrimaryLanguageTranslation(language, values.usageInstructions, offer?.conditions_of_use_and_storage)
      : undefined,
    country_of_origin: values.countryOfOrigin
      ? updatePrimaryLanguageTranslation(language, values.countryOfOrigin, offer?.country_of_origin)
      : undefined,
    deposit_permutation_id:
      values.weightType !== 'weighted-items' && values.depositOption ? values.depositOption : undefined,
    description: values.description
      ? updatePrimaryLanguageTranslation(language, values.description, offer?.description)
      : undefined,
    distributor_information: values.distributorInfo
      ? updatePrimaryLanguageTranslation(language, values.distributorInfo, offer?.distributor_information)
      : undefined,
    ingredients: values.ingredients
      ? updatePrimaryLanguageTranslation(language, values.ingredients, offer?.ingredients)
      : undefined,
    is_bundle_offer: values.bundleOffer,
    more_information: values.moreInformation
      ? updatePrimaryLanguageTranslation(language, values.moreInformation, offer?.more_information)
      : undefined,
    name: updatePrimaryLanguageTranslation(language, values.name, offer?.name),
    nutrition_facts:
      nutritionFactsFormat === 'text' && values.nutritionFacts
        ? updatePrimaryLanguageTranslation(language, values.nutritionFacts, offer?.nutrition_facts)
        : undefined,
    producer_information: values.producerInfo
      ? updatePrimaryLanguageTranslation(language, values.producerInfo, offer?.producer_information)
      : undefined,
    image: imageUrls.length > 0 ? imageUrls[0] : undefined,
    images: imageUrls.length > 0 ? imageUrls.map(url => ({ url })) : undefined,
    option_bindings: values.weightType === 'weighted-items' ? undefined : optionBindings,
    external_info_hyperlink:
      (values.isOTCRestricted || settings.externalInfoHyperlinkAllowed) &&
      values.externalInfoHyperlink?.hyperlink &&
      values.externalInfoHyperlink?.text
        ? {
            text: updatePrimaryLanguageTranslation(
              language,
              values.externalInfoHyperlink.text,
              offer?.external_info_hyperlink?.text,
            ),
            hyperlink: values.externalInfoHyperlink.hyperlink,
          }
        : undefined,
    restrictions: [
      values.isOTCRestricted ? { type: 'over_the_counter' as const } : undefined,
      values.isAgeRestricted ? { type: 'age_restricted' as const, age_limit: values.ageLimit || undefined } : undefined,
      values.isDeliveryHandshakeRestricted ? { type: 'delivery_handshake' as const } : undefined,
    ].filter(isDefined),
  };
};

export const transformImagesApiToFormValues = (
  offer: Offer,
): { type: 'existing-image'; imageUrl: string; id: string }[] => {
  const uniqueImageUrls = [
    ...new Set([...(offer.image ? [offer.image] : []), ...(offer.images ?? []).map(img => img.url)]),
  ];
  return uniqueImageUrls.map(imageUrl => ({ type: 'existing-image', imageUrl, id: imageUrl }));
};

type BuildOverridesPayloadArgs = {
  values: ValidFormValues;
  currency: Currency;
  currOverriddenOfferAttrs: (keyof Offer)[];
  venueLevelOnlyAttrs?: (keyof Offer)[];
};

export const buildOverridesPayload = ({
  values,
  currency,
  currOverriddenOfferAttrs,
  venueLevelOnlyAttrs = [],
}: BuildOverridesPayloadArgs) => {
  const allPossibleOverrides: Partial<Offer> = {
    price: values.priceOverride
      ? sharedFieldsPayloadBuilder.price({
          price: values.priceOverride,
          currency,
          discountType: values.discountTypeOverride,
          discountPercentage: values.discountPercentageOverride,
          fixedDiscountPrice: values.fixedDiscountPriceOverride,
        })
      : undefined,
    discounted: sharedFieldsPayloadBuilder.discounted(values.discountTypeOverride, values.priceOverride),
    enabled: sharedFieldsPayloadBuilder.enabled(values.enabledOverride, values.availabilityPeriodOverride),
    external_id: sharedFieldsPayloadBuilder.external_id(values.posIdOverride),
    merchant_sku: sharedFieldsPayloadBuilder.merchant_sku(values.merchantSkuOverride),
    weekly_availability: sharedFieldsPayloadBuilder.weekly_availability(
      values.useWeeklyAvailabilityOverride,
      values.weeklyAvailabilityOverride,
    ),
    weekly_visibility: sharedFieldsPayloadBuilder.weekly_visibility(
      values.useWeeklyVisibilityOverride,
      values.weeklyVisibilityOverride,
    ),
    inventory_mode: getInventoryMode(values, 'inventoryModeOverride' as keyof ValidFormValues),
  };

  const offerAttrsPayload = currOverriddenOfferAttrs.concat(venueLevelOnlyAttrs).reduce<Partial<Offer>>(
    (acc, offerAttr) => ({
      ...acc,
      [offerAttr]: allPossibleOverrides[offerAttr],
    }),
    {},
  );

  /**
   * We need to pin intentional `null`/`undefined` separately as a different type of overrides (`fields_to_reset`) to identify which of these attributes we want to be synthesized into m2 instead of the Shared Menu's original "truthy" value.
   * This covers situations when the original offer (set at the Shared Menu level) has a custom value, let's say, for the `external_id` as "123"
   * But at the venue level, the venue wants to unset this value and keep it as `null`
   * If we don't put the `external_id` inside `fields_to_reset`, the BE will not save this nullish override and keep the original value "123" when synthesizing the offer into m2
   */
  const overridesToPinAsNull = getTypedKeys(offerAttrsPayload).filter(offerAttr => !offerAttrsPayload[offerAttr]);

  const offerAttrsWithoutNullishOverrides = getTypedKeys(offerAttrsPayload).reduce<Partial<Offer>>(
    (acc, offerAttr) =>
      overridesToPinAsNull.includes(offerAttr) ? acc : { ...acc, [offerAttr]: offerAttrsPayload[offerAttr] },
    {},
  );

  return {
    ...offerAttrsWithoutNullishOverrides,
    ...(overridesToPinAsNull.length ? { fields_to_reset: overridesToPinAsNull } : {}),
  };
};

export const transformOfferApiToFormValues = (
  offer: Offer,
  settings: {
    alcoholWarningPermilleLimit?: number;
    language: Language;
    categoryId: string | null;
    vatCategories?: VatCategory[];
    productInformationSettings?: ProductInformationSettings;
    offerOverrides?: OfferOverrides;
    itemBalance?: number;
    offerCommissions?: FullCommission;
  },
): FormValues => {
  const {
    language,
    categoryId,
    vatCategories,
    alcoholWarningPermilleLimit,
    productInformationSettings,
    offerOverrides,
    itemBalance,
    offerCommissions,
  } = settings;

  const nutritionValues =
    productInformationSettings?.nutrition_values
      ?.sort((a, b) => a.table_row_number - b.table_row_number)
      .map(setting => {
        const nutritionValue = (offer.nutrition_values ?? []).find(
          itemValue => itemValue.nutrition_value_id === setting.id,
        );
        if (!setting.ask_by_default && !nutritionValue) {
          return undefined;
        }
        const value = nutritionValue?.amount;
        return { id: setting.id, value };
      })
      .filter(isDefined) ?? [];

  const hasHighAlcoholContent =
    typeof alcoholWarningPermilleLimit === 'number' &&
    alcoholPercentageToPermilles(offer.alcohol_percentage) > alcoholWarningPermilleLimit;

  return {
    categoryId,
    name: findTranslation(offer.name, language),
    productHierarchyLevelId: offer.product_hierarchy_level_id || undefined,
    description: findTranslation(offer.description, language),
    price: offer.discounted ? offer.discounted.original_price : offer.price,
    discountType: offer.discounted?.discount_type || undefined,
    fixedDiscountPrice: offer.discounted ? offer.price : undefined,
    caffeineContentUnits: offer.caffeine_content?.units,
    caffeineContentValue: offer.caffeine_content?.value,
    dietaryPreferences: offer.dietary_preferences || [],
    discountPercentage: offer.discounted
      ? getDiscountPercentage(offer.price, offer.discounted.original_price)
      : undefined,
    weightInGrams: offer.weight_in_grams || undefined,
    volumeInMl: offer.volume_in_ml || undefined,
    numberOfUnits: offer.number_of_units || undefined,
    displayUnit: offer.display_unit ?? null,
    vat: initialVatFieldValue(vatCategories, offer.vat_category_code || undefined, Number(offer.vat_percentage)),
    alcoholPercentage: offer.alcohol_percentage ?? 0,
    posId: offer.external_id || '',
    barcodeGtin: offer.barcode_gtin || '',
    merchantSku: offer.merchant_sku || '',
    source: (offer.source_config && offer.source_config.default_source) || undefined,
    sourceConfig: offer.source_config
      ? {
          additives: offer.source_config?.field_config.additives || undefined,
          alcoholPercentage: offer.source_config?.field_config.alcohol_percentage || undefined,
          allergens: offer.source_config?.field_config.allergens || undefined,
          distributorInfo: offer.source_config?.field_config.distributor_information || undefined,
          image: offer.source_config?.field_config.images || undefined,
          ingredients: offer.source_config?.field_config.ingredients || undefined,
          name: offer.source_config?.field_config.name || undefined,
          numberOfUnits: offer.source_config?.field_config.number_of_units || undefined,
          nutritionFields:
            offer.source_config?.field_config.nutrition_facts ||
            offer.source_config?.field_config.nutrition_values_baseline ||
            offer.source_config?.field_config.nutrition_values ||
            undefined,
          countryOfOrigin: offer.source_config?.field_config.country_of_origin || undefined,
          displayUnit: offer.source_config?.field_config.display_unit || undefined,
          usageInstructions: offer.source_config?.field_config.conditions_of_use_and_storage || undefined,
          producerInfo: offer.source_config?.field_config.producer_information || undefined,
          volumeInMl: offer.source_config?.field_config.volume_in_ml || undefined,
          weightInGrams: offer.source_config?.field_config.weight_in_grams || undefined,
          productHierarchyLevelId: offer.source_config?.field_config.product_hierarchy_level_id || undefined,
        }
      : undefined,
    image: transformImagesApiToFormValues(offer),
    ingredients: findTranslation(offer.ingredients, language),
    additives: findTranslation(offer.additives, language),
    allergens: findTranslation(offer.allergens, language),
    mandatoryWarnings: offer.mandatory_warnings || [],
    moreInformation: findTranslation(offer.more_information, language),
    nutritionFacts: findTranslation(offer.nutrition_facts, language),
    nutritionValues,
    nutritionValuesBaseline: offer.nutrition_values_baseline || undefined,
    countryOfOrigin: findTranslation(offer.country_of_origin, language),
    usageInstructions: findTranslation(offer.conditions_of_use_and_storage, language),
    producerInfo: findTranslation(offer.producer_information, language),
    distributorInfo: findTranslation(offer.distributor_information, language),
    bundleOffer: !!offer.is_bundle_offer,
    depositOption: offer.deposit_permutation_id || '',
    enabled: offer.enabled.enabled,
    availabilityPeriod: {
      start: offer.enabled.after ?? null,
      end: offer.enabled.before ?? null,
    },
    useWeeklyAvailability: !!(offer.weekly_availability && offer.weekly_availability.length > 0),
    weeklyAvailability:
      offer.weekly_availability && offer.weekly_availability.length > 0
        ? fromWeeklyTimeSpansToWeeklyScheduleInputEntries(offer.weekly_availability)
        : [EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY],
    useWeeklyVisibility: !!(offer.weekly_visibility && offer.weekly_visibility.length > 0),
    weeklyVisibility:
      offer.weekly_visibility && offer.weekly_visibility.length > 0
        ? fromWeeklyTimeSpansToWeeklyScheduleInputEntries(offer.weekly_visibility)
        : [EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY],
    userRequirement:
      !!offer.user_requirements && offer.user_requirements.length > 0 ? offer.user_requirements[0] : undefined,
    maxQuantityPerPurchase: offer.max_quantity_per_purchase || undefined,
    minQuantityPerPurchase: offer.min_quantity_per_purchase || undefined,
    enableEatin: !!offer.delivery_methods?.includes('eatin'),
    enableTakeaway: !!offer.delivery_methods?.includes('takeaway'),
    enableDelivery: !!offer.delivery_methods?.includes('homedelivery'),
    disableBikeDelivery: !!offer.courier_restrictions?.includes('no_bikes'),
    disableCarDelivery: !!offer.courier_restrictions?.includes('no_cars'),
    disableMotorcycleDelivery: !!offer.courier_restrictions?.includes('no_motorcycles'),
    isRestrictedItem: !!(offer.restrictions && offer.restrictions.length > 0),
    isWoltPlusOnly: offer.wolt_plus_only,
    isOTCRestricted: !!offer.restrictions?.some(restriction => restriction.type === 'over_the_counter'),
    isAgeRestricted:
      offer.restrictions?.some(restriction => restriction.type === 'age_restricted') || hasHighAlcoholContent,
    ageLimit: (offer.restrictions ?? []).find(isAgeRestriction)?.age_limit || undefined,
    isDeliveryHandshakeRestricted: !!offer.restrictions?.some(restriction => restriction.type === 'delivery_handshake'),
    location: undefined,
    pickingAreaId: undefined,
    pickingItemSequence: undefined,
    weightType: !!offer.sell_by_weight_config ? 'weighted-items' : 'pre-packaged',
    sellByWeightInputType: offer.sell_by_weight_config?.input_type || undefined,
    gramsPerStep: offer.sell_by_weight_config?.grams_per_step || undefined,
    externalInfoHyperlink: !!offer.external_info_hyperlink
      ? {
          hyperlink: offer.external_info_hyperlink.hyperlink,
          text: findTranslation(offer.external_info_hyperlink.text, language),
        }
      : undefined,
    optionBindings: offer.option_bindings ?? [],
    brandId: offer.brand_id ?? null,
    inventoryMode:
      offer.inventory_mode && offer.inventory_mode !== 'forced_out_of_stock' ? offer.inventory_mode : 'binary',
    inStock: offer.inventory_mode !== 'forced_out_of_stock',
    inventoryBalance: itemBalance,
    homedeliveryCommission: offerCommissions?.homedelivery?.percentage,
    eatinCommission: offerCommissions?.eatin?.percentage,
    takeawayCommission: offerCommissions?.takeaway?.percentage,
    ...transformOverridesToOverrideFormValues(offerOverrides),
  };
};

const transformOverridesToOverrideFormValues = (
  offerOverrides?: OfferOverrides,
): OverrideFormValues & Pick<InventoryManagementFormValues, 'inventoryModeOverride' | 'inStockOverride'> => {
  return {
    enabledOverride: !!offerOverrides?.enabled?.enabled,
    useWeeklyAvailabilityOverride: !!(
      offerOverrides?.weekly_availability && offerOverrides?.weekly_availability.length > 0
    ),
    useWeeklyVisibilityOverride: !!(offerOverrides?.weekly_visibility && offerOverrides?.weekly_visibility.length > 0),
    priceOverride: offerOverrides?.discounted ? offerOverrides?.discounted.original_price : offerOverrides?.price,
    fixedDiscountPriceOverride: offerOverrides?.discounted ? offerOverrides?.price : undefined,
    discountPercentageOverride:
      offerOverrides?.discounted && offerOverrides?.price
        ? getDiscountPercentage(offerOverrides.price, offerOverrides.discounted.original_price)
        : undefined,
    discountTypeOverride: offerOverrides?.discounted?.discount_type || undefined,
    posIdOverride: offerOverrides?.external_id || '',
    weeklyAvailabilityOverride:
      offerOverrides?.weekly_availability && offerOverrides?.weekly_availability.length > 0
        ? fromWeeklyTimeSpansToWeeklyScheduleInputEntries(offerOverrides?.weekly_availability)
        : [EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY],
    weeklyVisibilityOverride:
      offerOverrides?.weekly_visibility && offerOverrides?.weekly_visibility.length > 0
        ? fromWeeklyTimeSpansToWeeklyScheduleInputEntries(offerOverrides?.weekly_visibility)
        : [EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY],
    merchantSkuOverride: offerOverrides?.merchant_sku || '',
    availabilityPeriodOverride: {
      start: offerOverrides?.enabled?.after ?? null,
      end: offerOverrides?.enabled?.before ?? null,
    },
    inventoryModeOverride:
      offerOverrides?.inventory_mode && offerOverrides.inventory_mode !== 'forced_out_of_stock'
        ? offerOverrides.inventory_mode
        : 'binary',
    inStockOverride: offerOverrides?.inventory_mode !== 'forced_out_of_stock',
  };
};

export const isAgeRestriction = (restriction: OTCRestriction | AgeRestriction): restriction is AgeRestriction =>
  restriction.type === 'age_restricted';

export const buildNewOfferFormValues = (
  categoryId: string | null,
  vat: VatFieldValue = initialVatFieldValue(undefined, undefined),
  nutritionSettings: NutritionSetting[] = [],
): FormValues => {
  return {
    additives: '',
    alcoholPercentage: 0,
    allergens: '',
    availabilityPeriod: { start: null, end: null },
    barcodeGtin: '',
    brandId: null,
    bundleOffer: false,
    caffeineContentUnits: undefined,
    caffeineContentValue: undefined,
    categoryId,
    countryOfOrigin: '',
    depositOption: '',
    description: '',
    dietaryPreferences: [],
    disableBikeDelivery: false,
    disableCarDelivery: false,
    disableMotorcycleDelivery: false,
    fixedDiscountPrice: undefined,
    discountPercentage: undefined,
    discountType: undefined,
    distributorInfo: '',
    enabled: true,
    enableDelivery: true,
    enableEatin: true,
    enableTakeaway: true,
    isRestrictedItem: false,
    isOTCRestricted: false,
    isAgeRestricted: false,
    ageLimit: undefined,
    isDeliveryHandshakeRestricted: false,
    image: [],
    ingredients: '',
    merchantSku: '',
    mandatoryWarnings: [],
    moreInformation: '',
    name: '',
    nutritionFacts: '',
    nutritionValues: nutritionSettings
      .filter(value => value.ask_by_default)
      .map(value => ({ id: value.id, value: undefined })),
    posId: '',
    price: undefined,
    producerInfo: '',
    productHierarchyLevelId: undefined,
    source: undefined,
    sourceConfig: undefined,
    usageInstructions: '',
    userRequirement: undefined,
    useWeeklyAvailability: false,
    useWeeklyVisibility: false,
    vat,
    weeklyAvailability: [],
    weeklyVisibility: [],
    numberOfUnits: undefined,
    volumeInMl: undefined,
    weightInGrams: undefined,
    displayUnit: null,
    maxQuantityPerPurchase: undefined,
    minQuantityPerPurchase: undefined,
    location: undefined,
    pickingAreaId: undefined,
    pickingItemSequence: undefined,
    weightType: 'pre-packaged',
    sellByWeightInputType: undefined,
    gramsPerStep: undefined,
    externalInfoHyperlink: undefined,
    isWoltPlusOnly: false,
    optionBindings: [],
    inventoryMode: 'binary',
    inStock: true,
    homedeliveryCommission: undefined,
    eatinCommission: undefined,
    takeawayCommission: undefined,
  };
};

const transformFeedProductImagesToFormValues = (product: Product): FeedFormValues['image'] =>
  product.images?.map(({ url }) => ({
    id: url,
    type: 'existing-image',
    imageUrl: url,
  })) ?? [];

export const transformFeedProductToFormValues = (
  product: Product,
  options: { language: Language; nutritionSettings: NutritionSetting[] },
): FeedFormValues => {
  const { language, nutritionSettings } = options;
  return {
    additives: findTranslation(product.additives, language),
    alcoholPercentage: product.alcoholPermille ? product.alcoholPermille / 10 : 0,
    allergens: findTranslation(product.allergens, language),
    brandId: product.brandId,
    description: findTranslation(product.description, language),
    dietaryPreferences:
      product.dietaryPreferences?.map(preference => preference.toLowerCase() as SharedMenuDietaryPreference) || [],
    distributorInfo: findTranslation(product.distributorInformation, language),
    image: transformFeedProductImagesToFormValues(product),
    ingredients: findTranslation(product.ingredients, language),
    mandatoryWarnings:
      product.mandatoryWarnings?.map(warning => warning.toLowerCase() as SharedMenuMandatoryWarning) || [],
    moreInformation: findTranslation(product.moreInformation, language),
    name: findTranslation(product.name, language),
    numberOfUnits: product.numberOfUnits,
    nutritionFacts: findTranslation(product.nutritionFacts, language),
    nutritionValuesBaseline:
      product.nutritionValues?.baseline.toLowerCase() as AdvancedDetails['nutritionValuesBaseline'],
    nutritionValues: (product.nutritionValues?.nutritionValues || [])
      .map(value => ({ id: value.nutritionValueId, value: value.amount }))
      .sort(
        (valueA, valueB) =>
          (nutritionSettings.find(setting => setting.id === valueA.id)?.table_row_number || 0) -
          (nutritionSettings.find(setting => setting.id === valueB.id)?.table_row_number || 0),
      ),
    countryOfOrigin: findTranslation(product.countryOfOrigin || [], language),
    usageInstructions: findTranslation(product.conditionsOfUseAndStorage || [], language),
    producerInfo: findTranslation(product.producerInformation, language),
    volumeInMl: product.volumeInMl,
    weightInGrams: product.weightInGrams,
    productHierarchyLevelId: product.productHierarchyLevelId,
    displayUnit: product.displayUnit ? (product.displayUnit.toLowerCase() as ItemDisplayUnit) : null,
    productId: product.id,
  };
};
